export type SubmissionStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'needs_info'
  | 'accepted'
  | 'declined'
  | 'published'
  | 'archived'

export type ProcessingStatus =
  | 'queued'
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'error'

export interface SocialLinks {
  instagram: string
  facebook: string
  x: string
  website: string
}

export interface ContributorDetails {
  contactName: string
  contactEmail: string
  creditName: string
  socialLinks: SocialLinks
}

export interface UploadAsset {
  id: string
  name: string
  size: number
  type: string
  previewUrl: string
  storagePath?: string
  progress: number
  status: ProcessingStatus
  error?: string
  file?: File
}

export interface StrainDraft {
  id: string
  name: string
  growerOrBrand: string
  notes: string
  assets: UploadAsset[]
}

export interface SubmissionDraft {
  id?: string
  ownerUid?: string
  details: ContributorDetails
  strains: StrainDraft[]
  consentAccepted: boolean
  releaseVersion: string
  status: SubmissionStatus
  step: 1 | 2 | 3
  updatedAt?: string
}

export interface AdminAsset {
  id: string
  name: string
  previewUrl: string
  originalPath?: string
  status: ProcessingStatus
}

export interface AdminStrain {
  id: string
  name: string
  growerOrBrand: string
  notes: string
  imageCount: number
  assets: AdminAsset[]
}

export type SubmissionRating = 1 | 2 | 3 | 4 | 5

export type UsageChannel = 'newsletter' | 'strainProfile' | 'homepage'

export interface UsageRecord {
  active: boolean
  lastUsedAt?: Date
}

export type SubmissionUsage = Record<UsageChannel, UsageRecord>

export interface EditorialMetadataPatch {
  rating?: SubmissionRating | null
  tags?: string[]
  internalNote?: string
  usage?: Partial<Record<UsageChannel, boolean>>
}

export interface AdminSubmission {
  id: string
  contactName: string
  contactEmail: string
  creditName: string
  socialLinks: SocialLinks
  status: Exclude<SubmissionStatus, 'draft'>
  submittedAt: Date
  internalNote: string
  rating: SubmissionRating | null
  tags: string[]
  usage: SubmissionUsage
  strains: AdminStrain[]
}

export const RELEASE_VERSION = '2026-08-07-v2'

export function newId(): string {
  return crypto.randomUUID()
}

export function createEmptyStrain(): StrainDraft {
  return {
    id: newId(),
    name: '',
    growerOrBrand: '',
    notes: '',
    assets: [],
  }
}

export function createEmptyDraft(): SubmissionDraft {
  return {
    details: {
      contactName: '',
      contactEmail: '',
      creditName: '',
      socialLinks: {
        instagram: '',
        facebook: '',
        x: '',
        website: '',
      },
    },
    strains: [createEmptyStrain()],
    consentAccepted: false,
    releaseVersion: RELEASE_VERSION,
    status: 'draft',
    step: 1,
  }
}

export function imageCount(draft: Pick<SubmissionDraft, 'strains'>): number {
  return draft.strains.reduce((total, strain) => total + strain.assets.length, 0)
}
